import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, BadgeCheck, Fuel, Gauge, Cog, Palette, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { fetchMerchantItem } from '../hooks/useMerchantMarket';
import PriceDisplay from '../components/marketplace/PriceDisplay';
import ContactActions from '../components/marketplace/ContactActions';
import StoreUidBadge from '../components/marketplace/StoreUidBadge';
import SafetyTips from '../components/marketplace/SafetyTips';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=60';

export default function CarDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const cachedStore = location.state?.store || null;
  const cachedItem = location.state?.item || null;
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [data, setData] = useState({ item: cachedItem, store: cachedStore });
  const [loading, setLoading] = useState(!cachedItem);
  const [activeImg, setActiveImg] = useState(0);
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  useEffect(() => {
    let mounted = true;
    fetchMerchantItem('auto', id, cachedStore)
      .then(res => { if (mounted) setData((prev) => ({ item: res.item || prev.item, store: res.store || prev.store })); })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>;
  if (!data.item) return <div className="min-h-screen flex items-center justify-center text-red-500">Not found</div>;
  const { item, store } = data;
  const images = (item.images && item.images.length) ? item.images : [item.image || PLACEHOLDER];
  const title = `${item.make || ''} ${item.model || ''} ${item.year || ''}`.trim();

  const spec = (label, value, Icon) => value == null || value === '' ? null : (
    <div className={`rounded-xl p-3 flex items-center gap-2 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
      {Icon && <Icon className="h-4 w-4 text-[#1E9BD9]" />}
      <div className="text-xs">
        <div className="text-slate-500">{label}</div>
        <div className="font-semibold text-[#0F172A] dark:text-white">{value}</div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/cars" className="inline-flex items-center gap-1.5 text-sm text-[#1E9BD9] hover:underline mb-6" data-testid="back-link">
          <BackIcon className="h-4 w-4" />
          {isAr ? 'العودة إلى السيارات' : 'Back to Cars'}
        </Link>

        <div className={`rounded-3xl overflow-hidden ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-lg'}`}>
          <div className="aspect-[16/9] bg-slate-100 dark:bg-white/5 overflow-hidden">
            <img src={images[activeImg]} alt={title} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? 'border-[#1E9BD9]' : 'border-transparent'}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] dark:text-white mb-2">{title}</h1>
                {item.trim && <p className="text-sm text-slate-500">{item.trim}</p>}
              </div>
              <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="lg" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {spec(isAr ? 'سنة الصنع' : 'Year', item.year)}
              {spec(isAr ? 'المسافة' : 'Mileage', item.mileage != null ? `${Number(item.mileage).toLocaleString()} km` : null, Gauge)}
              {spec(isAr ? 'الوقود' : 'Fuel', item.fuel_type, Fuel)}
              {spec(isAr ? 'ناقل الحركة' : 'Transmission', item.transmission, Cog)}
              {spec(isAr ? 'اللون' : 'Color', isAr ? item.color : (item.colorEn || item.color), Palette)}
              {spec(isAr ? 'الحالة' : 'Condition', item.condition)}
              {spec(isAr ? 'المواصفات' : 'Specs', item.regional_specs)}
              {spec(isAr ? 'الضمان' : 'Warranty', item.hasWarranty || item.warranty ? (isAr ? 'نعم' : 'Yes') : null, ShieldCheck)}
            </div>

            {item.description && (
              <div className={`rounded-xl p-4 mb-6 text-sm leading-relaxed ${isDarkMode ? 'bg-white/5 text-white/80' : 'bg-slate-50 text-slate-700'}`}>
                {item.description}
              </div>
            )}

            {store?.id && (
              <div className={`rounded-xl p-4 mb-4 flex items-center gap-3 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <Link to={`/store/${store.id}`} className="flex items-center gap-3 min-w-0" data-testid="store-strip">
                  {store.logo && <img src={store.logo} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[#0F172A] dark:text-white truncate">
                        {isAr ? (store.name || store.nameEn) : (store.nameEn || store.name)}
                      </span>
                      {store.verified && <BadgeCheck className="h-4 w-4 text-[#1E9BD9] shrink-0" />}
                    </div>
                    <StoreUidBadge store={store} locale={isAr ? 'ar' : 'en'} className="mt-1" />
                  </div>
                </Link>
              </div>
            )}

            <div className="mb-6">
              <SafetyTips category="cars" isAr={isAr} isDarkMode={isDarkMode} />
            </div>

            <ContactActions store={store} itemTitle={title} locale={isAr ? 'ar' : 'en'} />
          </div>
        </div>
      </div>
    </div>
  );
}
